/**
 * ReverseMortgageStrategyAllocator.tsx – reverse mortgage details (loan amount, LVR, interest, structure, equity impact).
 * Used by AgedCareAnalyser in App.js. Props: factFind, details, setDetails, legacyYears (optional).
 */
import React, { useState, Dispatch, SetStateAction } from 'react';
import { Info, ChevronDown, TrendingDown, TrendingUp } from 'lucide-react';
import { InfoIcon, CurrencyInput } from '../../components/ui/index.tsx';
import { formatCurrency, calculateAge } from '../../utils/index.ts';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import { DEEMING_RATE_LOWER, DEEMING_RATE_UPPER, DEEMING_THRESHOLDS } from '../../../../shared/src/calculation/constants/ratesAndThresholds.ts';

// The deeming figures in the help text come from the live rate table, so an indexation cannot leave
// the screen quoting last year's (it did, for two indexations).
const deemingLowerPct = `${(DEEMING_RATE_LOWER * 100).toFixed(2)}%`;
const deemingUpperPct = `${(DEEMING_RATE_UPPER * 100).toFixed(2)}%`;

import { REVERSE_MORTGAGE_DEFAULT_RATE } from '../../../../shared/src/calculation/support/capitalisingLoans.ts';
export interface ReverseMortgageDetails {
    loanAmount?: number | string;
    loanPercentage?: number | string;
    interestRate?: number | string;
    loanStructure?: 'lump_sum' | 'regular_payments';
    regularPaymentAmount?: number | string;
    regularPaymentFrequency?: 'fortnightly' | 'monthly';
    establishmentFees?: number | string;
    useForRad?: boolean;
}

type ReverseMortgageField = keyof ReverseMortgageDetails;

interface ReverseMortgageStrategyAllocatorProps {
    factFind: DraftFactFind;
    details: ReverseMortgageDetails;
    setDetails: Dispatch<SetStateAction<ReverseMortgageDetails>>;
    legacyYears?: number;
}

function ReverseMortgageStrategyAllocator({ factFind, details, setDetails, legacyYears: legacyYearsProp }: ReverseMortgageStrategyAllocatorProps) {
    const [isEducationExpanded, setIsEducationExpanded] = useState(false);
    const legacyYears = legacyYearsProp ?? 5;

    const handleDetailChange = (field: ReverseMortgageField, value: string) => {
        if (field === 'loanPercentage') {
            const roundedPercentage = Math.round(parseFloat(value) || 0);
            const homeValue = parseFloat(String(factFind.home?.value)) || 0;
            const loanAmount = homeValue * (roundedPercentage / 100);
            setDetails(prev => ({ ...prev, loanPercentage: roundedPercentage, loanAmount }));
        } else if (field === 'loanAmount') {
            const num = value === '' || value === undefined ? 0 : Number(value);
            const loanAmount = Number.isFinite(num) ? Math.max(0, num) : 0;
            const homeValue = parseFloat(String(factFind.home?.value)) || 0;
            setDetails(prev => {
                const next: ReverseMortgageDetails = { ...prev, loanAmount };
                if (homeValue > 0) {
                    next.loanPercentage = Math.round((loanAmount / homeValue) * 100);
                }
                return next;
            });
        } else if (field === 'establishmentFees' || field === 'regularPaymentAmount') {
            const num = value === '' || value === undefined ? 0 : Number(value);
            const n = Number.isFinite(num) ? Math.max(0, num) : 0;
            setDetails(prev => ({ ...prev, [field]: n }));
        } else if (field === 'interestRate') {
            setDetails(prev => ({ ...prev, [field]: parseFloat(value) || 0 }));
        } else {
            setDetails(prev => ({ ...prev, [field]: value }));
        }
    };

    const homeValue = parseFloat(String(factFind.home?.value)) || 0;
    const clientAge = calculateAge(factFind.client?.dob) ?? 0;
    const partnerAge = factFind.adviceFor === 'couple' ? calculateAge(factFind.partner?.dob) : null;
    const youngestAge = partnerAge ? Math.min(clientAge, partnerAge) : clientAge;

    const getMaxLVR = (age: number) => {
        if (age >= 80) return 50;
        if (age >= 75) return 45;
        if (age >= 70) return 40;
        if (age >= 65) return 30;
        if (age >= 60) return 20;
        return 15;
    };

    const maxLVR = getMaxLVR(youngestAge);
    const maxLoanAmount = homeValue * (maxLVR / 100);
    const currentLoanAmount = Number(details.loanAmount) || 0;
    const currentLoanPercentage = Number(details.loanPercentage) || 0;
    const exceedsLimit = currentLoanPercentage > maxLVR;

    const homeGrowthRate = parseFloat(String(factFind.home?.homeGrowthRate)) || 5;
    const initialLoanBalance = currentLoanAmount || 0;
    const interestRate = parseFloat(String(details.interestRate)) || REVERSE_MORTGAGE_DEFAULT_RATE;

    let projectedHomeValueWithRM = homeValue;
    let projectedLoanBalance = initialLoanBalance;

    for (let year = 1; year <= legacyYears; year++) {
        projectedHomeValueWithRM = projectedHomeValueWithRM * (1 + homeGrowthRate / 100);
        projectedLoanBalance = projectedLoanBalance * (1 + interestRate / 100);
        if (details.loanStructure === 'regular_payments' && year >= 1) {
            const paymentAmount = parseFloat(String(details.regularPaymentAmount)) || 0;
            const frequency = details.regularPaymentFrequency || 'fortnightly';
            const annualPayment = frequency === 'fortnightly' ? paymentAmount * 26 : paymentAmount * 12;
            projectedLoanBalance += annualPayment;
        }
    }

    let projectedHomeValueNoRM = homeValue;
    for (let year = 1; year <= legacyYears; year++) {
        projectedHomeValueNoRM = projectedHomeValueNoRM * (1 + homeGrowthRate / 100);
    }

    const projectedEquityWithRM = Math.max(0, projectedHomeValueWithRM - projectedLoanBalance);
    const projectedEquityNoRM = projectedHomeValueNoRM;
    const equityDifference = projectedEquityNoRM - projectedEquityWithRM;
    const equityPercentageOwned = initialLoanBalance > 0 ? (projectedEquityWithRM / projectedHomeValueWithRM * 100) : 100;

    return (
        <div className="mt-4 p-4 bg-gray-50 rounded-lg border space-y-4">
            <h4 className="font-semibold text-lg">Reverse Mortgage Details</h4>

            <div className="border border-gray-200 rounded-lg overflow-hidden">
                <button
                    onClick={() => setIsEducationExpanded(!isEducationExpanded)}
                    className="w-full p-4 bg-gradient-to-r from-blue-50 to-indigo-50 hover:from-blue-100 hover:to-indigo-100 transition-all duration-200 flex items-center justify-between"
                >
                    <h5 className="font-semibold text-gray-900 flex items-center gap-2">
                        <Info className="w-5 h-5 text-blue-600" />
                        What is a Reverse Mortgage?
                    </h5>
                    {isEducationExpanded ? (
                        <ChevronDown className="w-5 h-5 text-gray-600 transform rotate-180 transition-transform" />
                    ) : (
                        <ChevronDown className="w-5 h-5 text-gray-600 transition-transform" />
                    )}
                </button>
                {isEducationExpanded && (
                    <div className="p-4 bg-white border-t border-gray-200 space-y-3">
                        <div className="text-sm text-gray-700 space-y-3">
                            <p>
                                A <strong>reverse mortgage</strong> is a loan that allows you to access the equity in your home without making regular repayments.
                                Instead, interest compounds on the loan balance, and the loan is typically repaid when you sell the home, move into aged care, or pass away.
                            </p>
                            <div>
                                <p className="font-semibold mb-2">Loan Structure Options:</p>
                                <div className="space-y-3 pl-4 border-l-2 border-blue-200">
                                    <div>
                                        <p className="font-medium text-blue-700">1. Lump Sum</p>
                                        <p className="text-xs text-gray-600 mt-1">
                                            You receive the full loan amount upfront. This gives you immediate access to cash, which can be used for large expenses like paying a RAD deposit.
                                            The entire loan amount immediately starts accruing compound interest, so your loan balance grows faster from the start.
                                        </p>
                                    </div>
                                    <div>
                                        <p className="font-medium text-blue-700">2. Regular Payments</p>
                                        <p className="text-xs text-gray-600 mt-1">
                                            You receive regular payments (fortnightly or monthly) over time. This provides a steady income stream but may be more expensive
                                            as the loan balance accumulates with each payment. Each payment increases your loan balance, which then compounds with interest.
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <p>
                                <strong>How it works:</strong> The loan balance grows over time due to compound interest,
                                which means your home equity (the difference between your home's value and the loan balance) decreases over time.
                            </p>
                            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                <p className="font-semibold text-blue-900 mb-2">Impact on Age Pension:</p>
                                <p className="text-xs text-gray-700 mb-2">
                                    When you receive reverse mortgage proceeds (lump sum or regular payments), these funds are added to your assessable assets for the Age Pension means test.
                                    The loan proceeds are subject to deeming rules, which means Centrelink assumes they earn income at prescribed rates (currently {deemingLowerPct} for the first {formatCurrency(DEEMING_THRESHOLDS.single)}
                                    for singles or {formatCurrency(DEEMING_THRESHOLDS.couple)} for pensioner couples, and {deemingUpperPct} above those thresholds). This deemed income is included in the Age Pension income test, which can reduce
                                    or eliminate your Age Pension entitlement.
                                </p>
                                <p className="text-xs text-gray-700">
                                    <strong>Important:</strong> If you use the loan proceeds to pay a RAD (Refundable Accommodation Deposit), the RAD is exempt from the Age Pension assets test,
                                    so this may minimise the impact on your pension. However, if the proceeds remain in your bank account or other investments, they will be fully assessable.
                                </p>
                            </div>
                            <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                <p className="font-semibold text-blue-900 mb-2">Impact on Aged Care Assessment:</p>
                                <p className="text-xs text-gray-700 mb-2">
                                    For aged care cost calculations, reverse mortgage proceeds are treated as assessable assets:
                                </p>
                                <ul className="text-xs text-gray-700 list-disc list-inside space-y-1 mb-2">
                                    <li><strong>HSC (Hotelling Supplement Contribution):</strong> If loan proceeds increase your assessable assets, this may increase your HSC contribution.
                                    The HSC is calculated based on your Means Tested Amount (MTA), which considers both your income and assets. Higher assets mean a higher MTA, potentially increasing your HSC.</li>
                                    <li><strong>NCCC (Needs-Based Care Contribution):</strong> Similar to HSC, if reverse mortgage proceeds increase your assessable assets, this may increase your NCCC.
                                    The NCCC is also calculated based on your MTA, so higher assets can result in higher care contributions.</li>
                                </ul>
                                <p className="text-xs text-gray-700">
                                    <strong>Strategy consideration:</strong> Using reverse mortgage proceeds to pay a RAD can be beneficial because:
                                    (1) The RAD is exempt from the Age Pension assets test, and (2) The RAD reduces your DAP (Daily Accommodation Payment), which may offset increased HSC/NCCC costs.
                                </p>
                            </div>
                            <p className="mt-4">
                                <strong>Key considerations:</strong> Reverse mortgages can provide access to funds for living expenses, home modifications, or aged care costs, but they reduce the
                                equity available for your estate or future needs. The loan must be repaid when the property is sold or you move permanently.
                            </p>
                        </div>
                    </div>
                )}
            </div>

            {exceedsLimit && (
                <div className="p-3 bg-red-50 border-l-4 border-red-400 rounded-r-lg text-sm">
                    <p className="text-red-700 font-semibold">⚠️ Warning: Your loan amount exceeds typical lender limits for your age ({youngestAge}).</p>
                    <p className="text-red-600 mt-1 text-xs">Most lenders restrict reverse mortgages to a maximum of {maxLVR}% of home value ({formatCurrency(maxLoanAmount)}) based on the youngest borrower's age. Consider consulting a financial adviser.</p>
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Loan Amount ($)</label>
                    <CurrencyInput
                        value={details.loanAmount ?? ''}
                        onChange={(v) => handleDetailChange('loanAmount', String(v))}
                        placeholder="0"
                        className=""
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                        Loan Percentage (%)
                        <InfoIcon text="This shows what percentage of your home's current value the loan amount represents. For example, if your home is worth $3,000,000 and you borrow $500,000, that's 17% of your home value." />
                    </label>
                    <input
                        type="number"
                        value={Math.round(Number(details.loanPercentage) || 0)}
                        onChange={(e) => handleDetailChange('loanPercentage', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="0"
                        max={maxLVR}
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Interest Rate (% p.a.)</label>
                    <input
                        type="number"
                        step="0.1"
                        value={details.interestRate || REVERSE_MORTGAGE_DEFAULT_RATE}
                        onChange={(e) => handleDetailChange('interestRate', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Loan Structure</label>
                    <select
                        value={details.loanStructure || 'lump_sum'}
                        onChange={(e) => handleDetailChange('loanStructure', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                        <option value="lump_sum">Lump Sum</option>
                        <option value="regular_payments">Regular Payments</option>
                    </select>
                </div>
            </div>

            {details.loanStructure === 'regular_payments' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Payment Amount ($)</label>
                        <CurrencyInput
                            value={details.regularPaymentAmount ?? ''}
                            onChange={(v) => handleDetailChange('regularPaymentAmount', String(v))}
                            placeholder="0"
                            className=""
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Payment Frequency</label>
                        <select
                            value={details.regularPaymentFrequency || 'fortnightly'}
                            onChange={(e) => handleDetailChange('regularPaymentFrequency', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                            <option value="fortnightly">Fortnightly</option>
                            <option value="monthly">Monthly</option>
                        </select>
                    </div>
                </div>
            )}

            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                    Establishment Fees ($)
                    <InfoIcon text="One-time fees charged by the lender to set up the reverse mortgage. These fees are typically deducted from the loan proceeds, reducing the net amount you receive. Common fees include application fees, valuation fees, and legal fees." />
                </label>
                <CurrencyInput
                    value={details.establishmentFees ?? ''}
                    onChange={(v) => handleDetailChange('establishmentFees', String(v))}
                    placeholder="0"
                    className=""
                />
            </div>

            {details.loanStructure === 'lump_sum' && (
                <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-400 rounded-lg shadow-sm">
                    <div className="flex items-start gap-3">
                        <input
                            type="checkbox"
                            id="useReverseMortgageForRad"
                            checked={details.useForRad || false}
                            onChange={(e) => setDetails(prev => ({ ...prev, useForRad: e.target.checked }))}
                            className="h-6 w-6 rounded border-2 border-blue-600 text-blue-600 focus:ring-blue-500 mt-0.5 flex-shrink-0"
                        />
                        <div className="flex-1">
                            <label htmlFor="useReverseMortgageForRad" className="text-base font-bold text-blue-900 cursor-pointer block mb-1">
                                Use this loan amount to fund the RAD?
                            </label>
                            <p className="text-sm text-gray-700">
                                If checked, the loan amount will be allocated to pay the Refundable Accommodation Deposit (RAD), reducing your Daily Accommodation Payment (DAP).
                                Any remaining loan amount after RAD payment will go to your bank account.
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {initialLoanBalance > 0 && (
                <div className="space-y-4">
                    <h5 className="font-semibold text-gray-900 text-lg">Impact on Home Equity</h5>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-lg">
                            <h6 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
                                <TrendingDown className="w-4 h-4" />
                                With Reverse Mortgage
                            </h6>
                            <div className="mb-4">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm text-gray-700">Home Equity Ownership</span>
                                    <span className="text-lg font-bold text-blue-700">{equityPercentageOwned.toFixed(1)}%</span>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-6 overflow-hidden">
                                    <div
                                        className="bg-gradient-to-r from-blue-500 to-indigo-600 h-6 flex items-center justify-end pr-2 transition-all duration-500"
                                        style={{ width: `${Math.min(100, equityPercentageOwned)}%` }}
                                    >
                                        {equityPercentageOwned > 15 && (
                                            <span className="text-xs font-semibold text-white">{equityPercentageOwned.toFixed(1)}%</span>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Projected Home Value:</span>
                                    <span className="font-medium">{formatCurrency(projectedHomeValueWithRM)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Loan Balance:</span>
                                    <span className="font-medium text-red-600">-{formatCurrency(projectedLoanBalance)}</span>
                                </div>
                                <div className="flex justify-between pt-2 border-t border-blue-200">
                                    <span className="font-semibold text-gray-900">Your Equity:</span>
                                    <span className="font-bold text-blue-700">{formatCurrency(projectedEquityWithRM)}</span>
                                </div>
                            </div>
                        </div>
                        <div className="p-4 bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200 rounded-lg">
                            <h6 className="font-semibold text-green-900 mb-3 flex items-center gap-2">
                                <TrendingUp className="w-4 h-4" />
                                Without Reverse Mortgage
                            </h6>
                            <div className="mb-4">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm text-gray-700">Home Equity Ownership</span>
                                    <span className="text-lg font-bold text-green-700">100%</span>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-6 overflow-hidden">
                                    <div className="bg-gradient-to-r from-green-500 to-emerald-600 h-6 flex items-center justify-end pr-2" style={{ width: '100%' }}>
                                        <span className="text-xs font-semibold text-white">100%</span>
                                    </div>
                                </div>
                            </div>
                            <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Projected Home Value:</span>
                                    <span className="font-medium">{formatCurrency(projectedHomeValueNoRM)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Loan Balance:</span>
                                    <span className="font-medium text-gray-400">$0</span>
                                </div>
                                <div className="flex justify-between pt-2 border-t border-green-200">
                                    <span className="font-semibold text-gray-900">Your Equity:</span>
                                    <span className="font-bold text-green-700">{formatCurrency(projectedEquityNoRM)}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="p-4 bg-gray-50 border border-gray-300 rounded-lg">
                        <div className="flex items-center justify-between mb-3">
                            <span className="font-semibold text-gray-900">Equity Impact After {legacyYears} Years:</span>
                            <span className="text-lg font-bold text-red-600">-{formatCurrency(equityDifference)}</span>
                        </div>
                        <p className="text-sm text-gray-600 mb-3">
                            By taking out a reverse mortgage, your home equity will be <strong>{formatCurrency(equityDifference)} less</strong> after {legacyYears} years compared to not taking the loan.
                        </p>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                                <span className="text-gray-600">With Reverse Mortgage:</span>
                                <span className="font-semibold text-blue-700 ml-2">{formatCurrency(projectedEquityWithRM)}</span>
                            </div>
                            <div>
                                <span className="text-gray-600">Without Reverse Mortgage:</span>
                                <span className="font-semibold text-green-700 ml-2">{formatCurrency(projectedEquityNoRM)}</span>
                            </div>
                        </div>
                        <p className="text-xs text-gray-500 mt-3">
                            Note: This projection assumes home growth at {homeGrowthRate}% p.a. and loan interest at {interestRate}% p.a.
                            Actual results may vary. Use the "View Projections" button for a detailed analysis including all assets.
                        </p>
                    </div>
                    <div className="p-4 bg-gray-50 border-l-4 border-gray-400 rounded-r-lg text-sm">
                        <p className="font-semibold text-gray-900 mb-2">Additional Considerations:</p>
                        <ul className="list-disc list-inside space-y-1 text-gray-700">
                            <li>Compound interest will erode your home equity over time</li>
                            <li>Loan balance grows faster as you age</li>
                            <li>May reduce equity available for aged care accommodation payments</li>
                            <li>Limited flexibility to move or refinance</li>
                            <li>Loan proceeds may be assessable for Age Pension assets test</li>
                        </ul>
                    </div>
                </div>
            )}
        </div>
    );
}

export default ReverseMortgageStrategyAllocator;
